/**
 * ResumeMetricsTile.tsx — STEP-DO-429-RESUME-TILE
 * Mobile-first 429 resume metrics tile.
 * UX-031: 44×44px minimum touch targets, 8px spacing.
 */
import type { ResumeMetrics } from "../api/types.js";

interface Props {
  data: ResumeMetrics;
}

function fmtSeconds(s: number | null): string {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.round(s / 60)}m`;
}

function fmtPct(rate: number | null): string {
  if (rate == null) return "—";
  return `${Math.round(rate * 100)}%`;
}

export function ResumeMetricsTile({ data }: Props) {
  return (
    <div data-testid="resume-metrics-tile" style={{ padding: "8px" }}>
      {/* Overall metrics row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
          marginBottom: 8,
        }}
      >
        {[
          { label: "P50 Resume Time", value: fmtSeconds(data.overall_p50_s) },
          { label: "P95 Resume Time", value: fmtSeconds(data.overall_p95_s) },
          { label: "Success Rate", value: fmtPct(data.overall_success_rate) },
        ].map((stat) => (
          <div
            key={stat.label}
            className="card"
            style={{
              minHeight: 44,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "10px 8px",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 20,
                fontWeight: 700,
                color: "var(--text)",
                lineHeight: 1,
              }}
            >
              {stat.value}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 8,
                color: "var(--text-dim)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginTop: 4,
                textAlign: "center",
              }}
            >
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Per-wave breakdown */}
      {data.per_wave_group.length > 0 && (
        <div>
          {data.per_wave_group.map((wg) => (
            <div
              key={wg.wave_group_id}
              className="card"
              style={{
                padding: "8px 14px",
                marginBottom: 8,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 44,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--text)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  marginRight: 8,
                }}
              >
                {wg.wave_group_id}
              </span>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  flexShrink: 0,
                }}
              >
                <span style={{ color: "var(--text-dim)" }}>
                  ×{wg.resume_count}
                </span>
                <span style={{ color: "var(--text)" }}>
                  p50:{fmtSeconds(wg.time_to_resume_p50_seconds)}
                </span>
                <span style={{ color: "var(--text)" }}>
                  ok:{fmtPct(wg.resume_success_rate)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.per_wave_group.length === 0 && (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text-dim)",
            padding: "8px 0",
          }}
        >
          No 429 resumes recorded in the last 7 days.
        </div>
      )}
    </div>
  );
}
